import type { IPOI } from '@/shared/api/types/poi-types'
import { Check, Loader2, Plus, Search, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@/shared/hooks/use-debounce'
import { Button } from '@/shared/ui/button'
import { DataTableFilterField } from '@/shared/ui/data-table'
import { FilterPopover } from '@/shared/ui/filter-popover'
import { Input } from '@/shared/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { resolvePoiCategoryName } from '@/shared/utils/poi-category-utils'

/**
 * Represents a language option in the POI selector.
 */
interface TourPoiLanguageOption {
  /** Display label for the language */
  label: string
  /** ISO language code (e.g., 'vi', 'en') */
  value: string
  /** Đánh dấu ngôn ngữ default của tenant để filter mặc định/reset đúng theo tenant tour. */
  isDefault?: boolean
}

/**
 * Props for the TourPoiSelector component.
 */
interface TourPoiSelectorQueryFilters {
  keyword?: string
  category?: string
  type?: string
  isActive?: boolean
}

interface TourPoiSelectorProps {
  /** List of all available POIs to select from */
  pois: IPOI[]
  /** List of POI IDs that are already selected */
  selectedPoiIds: string[]
  /** Callback when a POI is added */
  onAdd: (poi: IPOI) => void
  /** Callback when multiple POIs are added at once */
  onAddMany?: (pois: IPOI[]) => void
  /** Whether the selector is disabled */
  disabled?: boolean
  /** Whether the data is still loading */
  isLoading?: boolean
  /** Available language options for filtering POI content */
  languageOptions?: TourPoiLanguageOption[]
  /** Currently selected language code for POI display */
  selectedLanguageCode?: string
  /** Callback when the display language is changed */
  onLanguageChange?: (languageCode: string) => void
  /** Whether the next page is being fetched */
  isFetchingNextPage?: boolean
  /** Whether there are more pages to load */
  hasNextPage?: boolean
  /** Callback to load the next page */
  onLoadMore?: () => void
  /** Callback when filters should be applied to the POI query */
  onQueryFiltersChange?: (filters: TourPoiSelectorQueryFilters) => void
}

/**
 * Helper to extract the display name of a POI based on language priority.
 * Prefers Vietnamese ('vi'), then the first available localization, then falls back to ID.
 */
function getPoiName(poi: IPOI, selectedLanguageCode: string) {
  return (
    poi.localizedData.find((item) => item.language === selectedLanguageCode)?.name ??
    poi.localizedData[0]?.name ??
    poi.id
  )
}

/**
 * Component for searching, filtering, and selecting POIs to add to a tour.
 * Supports bulk selection and advanced filtering by category, type, and status.
 */
export function TourPoiSelector({
  pois,
  selectedPoiIds,
  onAdd,
  onAddMany,
  disabled,
  isLoading,
  languageOptions = [],
  selectedLanguageCode = '',
  onLanguageChange,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore,
  onQueryFiltersChange,
}: TourPoiSelectorProps) {
  const { t } = useTranslation()
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [search, setSearch] = useState('')
  const [checkedPoiIds, setCheckedPoiIds] = useState<string[]>([])
  const [selectedCategory, setSelectedCategory] = useState('__all__')
  const [selectedPoiType, setSelectedPoiType] = useState('__all__')
  const [selectedStatus, setSelectedStatus] = useState<'__all__' | 'active' | 'inactive'>('__all__')
  const debouncedSearch = useDebounce(search, 250)

  /** Khi chưa có selection, ưu tiên ngôn ngữ default của tenant; nếu tenant không đặt default thì lấy ngôn ngữ đầu danh sách. */
  const fallbackLanguageCode = useMemo(
    () => languageOptions.find((item) => item.isDefault)?.value ?? languageOptions[0]?.value ?? '',
    [languageOptions],
  )
  const effectiveLanguageCode = selectedLanguageCode || fallbackLanguageCode

  useEffect(() => {
    if (!selectedLanguageCode && fallbackLanguageCode) {
      onLanguageChange?.(fallbackLanguageCode)
    }
  }, [selectedLanguageCode, fallbackLanguageCode, onLanguageChange])

  useEffect(() => {
    onQueryFiltersChange?.({
      ...(debouncedSearch.trim() ? { keyword: debouncedSearch.trim() } : {}),
      ...(selectedCategory !== '__all__' ? { category: selectedCategory } : {}),
      ...(selectedPoiType !== '__all__' ? { type: selectedPoiType } : {}),
      ...(selectedStatus !== '__all__' ? { isActive: selectedStatus === 'active' } : {}),
    })
  }, [debouncedSearch, onQueryFiltersChange, selectedCategory, selectedPoiType, selectedStatus])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !onLoadMore) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) onLoadMore()
      },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, onLoadMore])

  const categoryOptions = useMemo(() => {
    const categories = new Map<string, string>()
    for (const poi of pois) {
      const value = poi.category?.trim()
      if (!value) {
        continue
      }
      categories.set(
        value,
        resolvePoiCategoryName(poi.categoryData, [effectiveLanguageCode]) || value,
      )
    }
    return Array.from(categories.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [effectiveLanguageCode, pois])

  const poiTypeOptions = useMemo(
    () =>
      Array.from(new Set(pois.map((item) => String(item.type ?? '').trim()).filter(Boolean))).sort(
        (a, b) => a.localeCompare(b),
      ),
    [pois],
  )

  const filtered = useMemo(() => {
    const raw = debouncedSearch.trim()
    const query = raw.toLowerCase()

    return pois.filter((poi) => {
      if (selectedCategory !== '__all__' && poi.category !== selectedCategory) {
        return false
      }

      if (selectedPoiType !== '__all__' && poi.type !== selectedPoiType) {
        return false
      }

      if (selectedStatus !== '__all__') {
        const isActive = selectedStatus === 'active'
        if (poi.isActive !== isActive) {
          return false
        }
      }

      if (!query) {
        return true
      }

      const name = getPoiName(poi, effectiveLanguageCode).toLowerCase()
      const id = poi.id.toLowerCase()
      const category = (poi.category ?? '').toLowerCase()
      const categoryName = resolvePoiCategoryName(poi.categoryData, [
        effectiveLanguageCode,
      ]).toLowerCase()
      const poiType = String(poi.type ?? '').toLowerCase()
      const destinationName = (poi.destinationName ?? '').toLowerCase()
      const address = (poi.address ?? '').toLowerCase()
      return (
        name.includes(query) ||
        id.includes(query) ||
        category.includes(query) ||
        categoryName.includes(query) ||
        poiType.includes(query) ||
        destinationName.includes(query) ||
        address.includes(query)
      )
    })
  }, [
    debouncedSearch,
    effectiveLanguageCode,
    pois,
    selectedCategory,
    selectedPoiType,
    selectedStatus,
  ])

  const selectedPoiIdSet = useMemo(() => new Set(selectedPoiIds), [selectedPoiIds])
  const availableCheckedCount = checkedPoiIds.filter((id) => !selectedPoiIdSet.has(id)).length
  const selectableFilteredPoiIds = useMemo(
    () => filtered.filter((item) => !selectedPoiIdSet.has(item.id)).map((item) => item.id),
    [filtered, selectedPoiIdSet],
  )
  const allFilteredChecked =
    selectableFilteredPoiIds.length > 0 &&
    selectableFilteredPoiIds.every((id) => checkedPoiIds.includes(id))

  function toggleChecked(poiId: string) {
    setCheckedPoiIds((prev) => {
      if (prev.includes(poiId)) return prev.filter((id) => id !== poiId)
      return [...prev, poiId]
    })
  }

  function handleAddSelected() {
    const ids = checkedPoiIds.filter((id) => !selectedPoiIdSet.has(id))
    if (ids.length === 0) return

    const poisToAdd = ids
      .map((id) => pois.find((poi) => poi.id === id))
      .filter((poi): poi is IPOI => Boolean(poi))
    if (poisToAdd.length === 0) return

    if (onAddMany) onAddMany(poisToAdd)
    else poisToAdd.forEach(onAdd)

    setCheckedPoiIds([])
  }

  function toggleCheckFiltered() {
    setCheckedPoiIds((prev) => {
      if (allFilteredChecked) {
        const removable = new Set(selectableFilteredPoiIds)
        return prev.filter((id) => !removable.has(id))
      }

      const next = new Set(prev)
      selectableFilteredPoiIds.forEach((id) => next.add(id))
      return Array.from(next)
    })
  }

  function resetFilters() {
    setSelectedCategory('__all__')
    setSelectedPoiType('__all__')
    setSelectedStatus('__all__')
    const defaultLanguage =
      languageOptions.find((item) => item.isDefault)?.value ?? languageOptions[0]?.value ?? ''
    setSearch('')
    onLanguageChange?.(defaultLanguage)
    setCheckedPoiIds([])
  }

  const activeFilterCount = [
    selectedLanguageCode || '',
    selectedCategory !== '__all__' ? selectedCategory : '',
    selectedPoiType !== '__all__' ? selectedPoiType : '',
    selectedStatus !== '__all__' ? selectedStatus : '',
  ].filter(Boolean).length

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            className="pl-8"
            placeholder={t('tour.searchPoi')}
            value={search}
            aria-label={t('tour.searchPoi')}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <FilterPopover
          title={t('common.filter')}
          resetLabel={t('common.reset')}
          activeCount={activeFilterCount}
          onReset={resetFilters}
        >
          <DataTableFilterField label={t('common.language')} className="min-w-0">
            <Select
              value={effectiveLanguageCode}
              onValueChange={(value) => onLanguageChange?.(value)}
              disabled={languageOptions.length < 1 || !onLanguageChange}
            >
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder={t('common.language')} />
              </SelectTrigger>
              <SelectContent>
                {languageOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('poi.category')} className="min-w-0">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder={t('poi.category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('common.all', { defaultValue: 'All' })}</SelectItem>
                {categoryOptions.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('poi.type', { defaultValue: 'Type' })} className="min-w-0">
            <Select value={selectedPoiType} onValueChange={setSelectedPoiType}>
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder={t('poi.type', { defaultValue: 'Type' })} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('common.all', { defaultValue: 'All' })}</SelectItem>
                {poiTypeOptions.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('common.status')} className="min-w-0">
            <Select
              value={selectedStatus}
              onValueChange={(value: '__all__' | 'active' | 'inactive') => setSelectedStatus(value)}
            >
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('common.all', { defaultValue: 'All' })}</SelectItem>
                <SelectItem value="active">{t('common.active')}</SelectItem>
                <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
              </SelectContent>
            </Select>
          </DataTableFilterField>
        </FilterPopover>
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-zinc-500">
          {`${t('tour.availablePois', { defaultValue: 'POI khả dụng' })}: ${filtered.length} / ${pois.length}`}
        </p>
        <div className="flex items-center gap-2">
          {search && (
            <Button size="sm" variant="ghost" onClick={() => setSearch('')}>
              <X size={12} className="mr-1" />
              {t('common.clear', { defaultValue: 'Clear' })}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            disabled={
              (disabled ?? false) || Boolean(isLoading) || selectableFilteredPoiIds.length === 0
            }
            onClick={toggleCheckFiltered}
          >
            {allFilteredChecked
              ? t('common.unselectAll', { defaultValue: 'Bỏ chọn tất cả' })
              : t('common.selectAll', { defaultValue: 'Chọn tất cả' })}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={(disabled ?? false) || Boolean(isLoading) || availableCheckedCount === 0}
            onClick={handleAddSelected}
          >
            <Plus size={12} className="mr-1" />
            {t('tour.addSelected', { defaultValue: 'Thêm đã chọn' })} ({availableCheckedCount})
          </Button>
        </div>
      </div>

      <div className="border border-zinc-200 rounded-lg divide-y divide-zinc-100 max-h-[420px] overflow-y-auto">
        {isLoading && (
          <div className="px-3 py-6 space-y-3">
            <p className="flex items-center justify-center gap-2 text-sm text-zinc-500">
              <Loader2 size={14} className="animate-spin" />
              {t('common.loading', { defaultValue: 'Đang tải dữ liệu...' })}
            </p>
            {['a', 'b', 'c', 'd'].map((item) => (
              <div
                key={`poi-loading-${item}`}
                className="h-12 rounded-md bg-zinc-100 animate-pulse"
              />
            ))}
          </div>
        )}
        {!isLoading &&
          filtered.map((poi) => {
            const added = selectedPoiIds.includes(poi.id)
            const checked = checkedPoiIds.includes(poi.id)
            const name = getPoiName(poi, effectiveLanguageCode)
            const categoryName = resolvePoiCategoryName(poi.categoryData, [effectiveLanguageCode])

            return (
              <div
                key={poi.id}
                className="flex items-start justify-between px-3 py-2.5 hover:bg-zinc-50 gap-3"
              >
                <div className="flex items-start gap-2 min-w-0">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-zinc-300"
                    checked={checked}
                    disabled={added || disabled}
                    onChange={() => toggleChecked(poi.id)}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-800 truncate">{name}</p>
                    <p className="text-xs text-zinc-500 truncate mt-0.5">
                      {poi.destinationName ??
                        t('tour.poiNoArea', { defaultValue: 'Chưa gán điểm đến' })}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-zinc-100 text-zinc-600 shrink-0">
                        {categoryName || poi.category || '—'}
                      </span>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 shrink-0">
                        {poi.type}
                      </span>
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium shrink-0 ${
                          poi.isActive
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-zinc-100 text-zinc-600'
                        }`}
                      >
                        {poi.isActive ? t('common.active') : t('common.inactive')}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={added ? 'ghost' : 'outline'}
                  disabled={added || disabled}
                  onClick={() => onAdd(poi)}
                  className="shrink-0 ml-2"
                >
                  {added ? (
                    <Check size={12} className="mr-1" />
                  ) : (
                    <Plus size={12} className="mr-1" />
                  )}
                  {added ? t('tour.added') : t('tour.addPoi')}
                </Button>
              </div>
            )
          })}
        {!isLoading && filtered.length === 0 && (
          <div className="px-3 py-6 text-center text-sm text-zinc-400">{t('common.noData')}</div>
        )}
        {isFetchingNextPage && (
          <div className="px-3 py-3 flex items-center justify-center gap-2 text-sm text-zinc-500">
            <Loader2 size={14} className="animate-spin" />
            {t('common.loading', { defaultValue: 'Đang tải...' })}
          </div>
        )}
        <div ref={sentinelRef} className="h-1" />
      </div>
    </div>
  )
}
